import React, { useState, useCallback } from 'react';
import { Alert, Box, Typography, Chip, Button, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AdminLayout from '../../components/AdminLayout';
import AdminTable from '../../components/AdminTable';
import ManageAccessDialog from './ManageAccessDialog';
import RoleChangeControl from './RoleChangeControl';
import { useAuth } from '../../contexts/AuthContext';
import apiClient from '../../services/apiClient';

// 999.18468 — PUT /api/admin/users/:id/status requires step-up (403 STEP_UP_REQUIRED).
function statusRefusalMessage(err) {
  if (err.data && err.data.error === 'STEP_UP_REQUIRED') {
    return "Changing a user's status needs a recent two-factor check. Sign out and back in with your authenticator code, then try again.";
  }
  return err.message;
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const [refreshKey, setRefreshKey] = useState(0);
  // 999.17683 — per-user application access (grant/revoke) dialog.
  const [accessUser, setAccessUser] = useState(null);
  const [statusError, setStatusError] = useState(null);

  const fetchUsers = useCallback(async (params) => {
    const qs = new URLSearchParams({
      limit: String(params.limit),
      offset: String(params.offset),
      sort_field: params.sort_field,
      sort_dir: params.sort_dir,
    });
    if (params.search) qs.set('search', params.search);
    if (params.status) qs.set('status', params.status);
    if (params.role) qs.set('role', params.role);

    const data = await apiClient.get(`/api/admin/users?${qs}`);
    return { rows: data.users || [], total: data.total || 0 };
  }, []);

  async function toggleStatus(user) {
    setStatusError(null);
    try {
      await apiClient.put(`/api/admin/users/${user.id}/status`, { is_active: !user.is_active });
    } catch (err) {
      setStatusError(statusRefusalMessage(err));
      return;
    }
    setRefreshKey(k => k + 1);
  }

  // Stops at the first refusal; users already changed stay changed, so refresh either way.
  async function bulkToggle(ids, enable) {
    setStatusError(null);
    try {
      for (const id of ids) {
        await apiClient.put(`/api/admin/users/${id}/status`, { is_active: enable });
      }
    } catch (err) {
      setStatusError(statusRefusalMessage(err));
    }
    setRefreshKey(k => k + 1);
  }

  const columns = [
    {
      id: 'id',
      label: 'UUID',
      minWidth: 120,
      defaultWidth: 160,
      render: (row) => (
        <Tooltip title={row.id} placement="top">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" sx={{ fontSize: '0.7rem', fontFamily: 'monospace', color: 'text.secondary' }}>
              {row.id.substring(0, 13)}...
            </Typography>
            <ContentCopyIcon
              sx={{ fontSize: 12, color: 'text.disabled', cursor: 'pointer', '&:hover': { color: 'text.primary' } }}
              onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(row.id); }}
            />
          </Box>
        </Tooltip>
      ),
    },
    { id: 'name', label: 'Name', minWidth: 120 },
    { id: 'email', label: 'Email', minWidth: 180 },
    {
      id: 'role', label: 'Role', minWidth: 80,
      render: (row) => (
        <Chip label={row.role} size="small" variant="outlined"
          color={row.role === 'admin' ? 'secondary' : 'default'}
          sx={{ fontSize: '0.7rem' }}
        />
      ),
    },
    {
      id: 'is_active', label: 'Status', minWidth: 80,
      render: (row) => (
        <Chip label={row.is_active ? 'Active' : 'Disabled'} size="small" variant="outlined"
          color={row.is_active ? 'success' : 'default'}
          sx={{ fontSize: '0.7rem' }}
        />
      ),
    },
    {
      id: 'totp_enabled', label: 'MFA', minWidth: 60, sortable: false,
      render: (row) => row.totp_enabled
        ? <Chip label="On" size="small" color="info" variant="outlined" sx={{ fontSize: '0.7rem' }} />
        : <Typography variant="caption" color="text.secondary">Off</Typography>,
    },
    {
      id: 'email_verified', label: 'Verified', minWidth: 70, sortable: false,
      render: (row) => row.email_verified ? 'Yes' : 'No',
    },
    {
      id: 'last_login', label: 'Last Login', minWidth: 130,
      render: (row) => formatDateTime(row.last_login),
    },
    {
      id: 'created_at', label: 'Created', minWidth: 110,
      render: (row) => formatDate(row.created_at),
    },
  ];

  const filters = [
    {
      id: 'status', label: 'Status',
      options: [{ value: 'active', label: 'Active' }, { value: 'disabled', label: 'Disabled' }],
    },
    {
      id: 'role', label: 'Role',
      options: [{ value: 'admin', label: 'Admin' }, { value: 'user', label: 'User' }],
    },
  ];

  return (
    <AdminLayout>
      <Typography variant="h4" sx={{ mb: 3, fontFamily: "'EB Garamond', serif" }}>
        User Management
      </Typography>

      {statusError && (
        <Alert severity="error" onClose={() => setStatusError(null)} sx={{ mb: 2 }}>
          {statusError}
        </Alert>
      )}

      <AdminTable
        tableId="admin_users"
        columns={columns}
        fetchData={fetchUsers}
        filters={filters}
        selectable
        bulkActions={[
          { label: 'Enable', color: 'success', onClick: (ids) => bulkToggle(ids, true) },
          { label: 'Disable', color: 'error', onClick: (ids) => bulkToggle(ids, false) },
        ]}
        actions={(row) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Button
              size="small" variant="outlined"
              color={row.is_active ? 'error' : 'success'}
              onClick={() => toggleStatus(row)}
              sx={{ fontSize: '0.65rem', py: 0.25, px: 1, minWidth: 0, textTransform: 'none' }}
            >
              {row.is_active ? 'Disable' : 'Enable'}
            </Button>
            <Button
              size="small" variant="outlined"
              onClick={() => setAccessUser(row)}
              sx={{ fontSize: '0.65rem', py: 0.25, px: 1, minWidth: 0, textTransform: 'none' }}
            >
              Manage access
            </Button>
            {/* 999.18427 — hidden on the current admin's own row */}
            <RoleChangeControl
              user={row}
              currentUserId={currentUser.id}
              onChanged={() => setRefreshKey(k => k + 1)}
            />
          </Box>
        )}
        refreshKey={refreshKey}
        defaultSort={{ field: 'created_at', dir: 'desc' }}
      />

      <ManageAccessDialog
        open={accessUser !== null}
        user={accessUser}
        onClose={() => setAccessUser(null)}
      />
    </AdminLayout>
  );
}
